/**
 * ClickHouse concept taxonomy — зеркало backend assessment_service.py
 * Используется в квизе и для построения Knowledge Graph / Roadmap.
 */

export type ConceptStatus = "known" | "learning" | "unknown";
export type RoadmapStatus = "known" | "next" | "to_learn";
export type AreaStatus = "known" | "in_progress" | "to_learn";

export interface Concept {
  id: string;
  label: string;
  brief: string;
}

export interface ConceptCategory {
  id: string;
  label: string;
  concepts: Concept[];
}

export interface RoadmapNode {
  id: string;
  label: string;
  category: string;
  category_label?: string;
  status: RoadmapStatus;
  depends_on: string[];
}

export interface RoadmapArea {
  id: string;
  label: string;
  description: string;
  status: AreaStatus;
  topics: RoadmapNode[];
}

export interface KnowledgeGraph {
  known: string[];
  learning: string[];
  unknown: string[];
}

export interface AssessmentResult {
  id: number;
  knowledge_graph: KnowledgeGraph;
  areas: RoadmapArea[];
  /** @deprecated плоский список, оставлен для совместимости */
  roadmap: RoadmapNode[];
  recommended_course_slug: string | null;
  summary: string | null;
  created_at: string;
}

// ─── Taxonomy ─────────────────────────────────────────────────────────────────

export const CONCEPT_TAXONOMY: ConceptCategory[] = [
  {
    id: "architecture",
    label: "Архитектура",
    concepts: [
      { id: "columnar_storage", label: "Колоночное хранение", brief: "Данные хранятся по столбцам, не строкам" },
      { id: "mergetree_internals", label: "Устройство MergeTree", brief: "Parts, granules, фоновый мердж" },
      { id: "lsm_parts", label: "Parts и LSM", brief: "Immutable части, которые объединяются фоново" },
      { id: "primary_index", label: "Первичный индекс", brief: "Разреженный индекс на уровне гранул" },
    ],
  },
  {
    id: "storage_engines",
    label: "Движки хранения",
    concepts: [
      { id: "mergetree", label: "MergeTree", brief: "Базовый движок. ORDER BY, PARTITION BY, TTL" },
      { id: "replacing_mergetree", label: "ReplacingMergeTree", brief: "Дедупликация по ORDER BY + version" },
      { id: "aggregating_mergetree", label: "AggregatingMergeTree", brief: "Хранит агрегированные состояния" },
      { id: "collapsing_mergetree", label: "CollapsingMergeTree", brief: "Мягкое удаление через sign=1/-1" },
      { id: "summing_mergetree", label: "SummingMergeTree", brief: "Суммирует числа при мерже" },
    ],
  },
  {
    id: "data_modeling",
    label: "Моделирование данных",
    concepts: [
      { id: "partitioning", label: "Партиционирование", brief: "Разбиение таблицы по выражению (дата, регион)" },
      { id: "sorting_key", label: "Ключ сортировки", brief: "ORDER BY = физический порядок + структура индекса" },
      { id: "ttl", label: "TTL", brief: "Автоудаление/перемещение данных по времени" },
      { id: "materialized_views", label: "Материализованные представления", brief: "Auto-обновляемые агрегаты" },
      { id: "dictionaries", label: "Словари", brief: "Быстрые lookup-таблицы в памяти" },
    ],
  },
  {
    id: "querying",
    label: "Запросы",
    concepts: [
      { id: "basic_select", label: "SELECT и базовый SQL", brief: "WHERE, ORDER BY, LIMIT, базовый синтаксис" },
      { id: "where_prewhere", label: "WHERE / PREWHERE", brief: "PREWHERE — фильтрация до загрузки данных с диска" },
      { id: "group_by", label: "GROUP BY", brief: "Агрегирование, ROLLUP, CUBE, GROUPING SETS" },
      { id: "aggregation_functions", label: "Агрегатные функции", brief: "count, sum, uniq, quantile и сотни других" },
      { id: "joins", label: "JOIN", brief: "INNER/LEFT/RIGHT JOIN. Большая таблица слева" },
    ],
  },
  {
    id: "advanced_querying",
    label: "Продвинутые запросы",
    concepts: [
      { id: "window_functions", label: "Оконные функции", brief: "rank(), row_number(), sum() OVER (...)" },
      { id: "array_functions", label: "Работа с массивами", brief: "arrayJoin, arrayMap, arrayFilter, has()" },
      { id: "cte", label: "CTE (WITH)", brief: "WITH ... AS (...) — именованные подзапросы" },
      { id: "lambda", label: "Lambda-функции", brief: "x -> expr для arrayMap, arrayFilter" },
    ],
  },
  {
    id: "operations",
    label: "Операции",
    concepts: [
      { id: "insert_patterns", label: "Паттерны INSERT", brief: "Батчинг, асинхронные вставки, буферизация" },
      { id: "mutations", label: "Мутации (ALTER)", brief: "ALTER TABLE UPDATE/DELETE — медленно, перезапись" },
      { id: "optimize", label: "OPTIMIZE TABLE", brief: "Принудительный мердж. Дедупликация с FINAL" },
      { id: "distributed_table", label: "Distributed таблица", brief: "Виртуальная таблица для кластера" },
      { id: "replication", label: "Репликация", brief: "ReplicatedMergeTree + ZooKeeper/CH Keeper" },
      { id: "sharding", label: "Шардирование", brief: "Горизонтальное масштабирование по шардам" },
    ],
  },
  {
    id: "performance",
    label: "Производительность",
    concepts: [
      { id: "sparse_indexes", label: "Разреженные индексы", brief: "Стандартный primary index на уровне granule" },
      { id: "bloom_filter", label: "Bloom filter индекс", brief: "Вероятностный индекс для точных совпадений" },
      { id: "minmax_index", label: "MinMax индекс", brief: "Хранит мин/макс гранулы для диапазонов" },
      { id: "compression", label: "Сжатие данных", brief: "LZ4, ZSTD, Delta — настройка по столбцам" },
      { id: "query_profiling", label: "Профилирование запросов", brief: "system.query_log, EXPLAIN, трассировка" },
    ],
  },
];

// Плоский список всех концепций для удобства
export const ALL_CONCEPTS: Concept[] = CONCEPT_TAXONOMY.flatMap((cat) => cat.concepts);

// Маппинг id -> label
export const CONCEPT_LABEL: Record<string, string> = Object.fromEntries(
  ALL_CONCEPTS.map((c) => [c.id, c.label])
);

// ─── Quiz Steps ───────────────────────────────────────────────────────────────

export type QuizStepType = "radio" | "radio_textarea" | "checkbox_grid" | "checkbox_grid_dynamic" | "checkbox_multi" | "radio_motivation";

export interface QuizOption {
  value: string;
  label: string;
}

export interface QuizStep {
  id: string;
  title: string;
  subtitle?: string;
  type: QuizStepType;
  options?: QuizOption[];
  freeTextLabel?: string;
  freeTextPlaceholder?: string;
  required?: boolean;
}

export const QUIZ_STEPS: QuizStep[] = [
  {
    id: "experience",
    title: "Твой опыт с ClickHouse",
    subtitle: "Как давно и насколько плотно ты работаешь с ClickHouse?",
    type: "radio",
    required: true,
    options: [
      { value: "never", label: "Никогда не слышал / только начинаю" },
      { value: "reading", label: "Читал документацию, но не использовал" },
      { value: "queries", label: "Пишу запросы (SELECT, базовый SQL)" },
      { value: "config", label: "Настраиваю таблицы, движки, партиции" },
      { value: "admin", label: "Администрирую кластеры, репликацию, мониторинг" },
    ],
  },
  {
    id: "scale",
    title: "Масштаб и задачи",
    subtitle: "Какой объём данных и тип задач?",
    type: "radio_textarea",
    required: true,
    options: [
      { value: "no_experience", label: "Нет опыта работы с данными" },
      { value: "local_test", label: "До 1 ГБ — локальные тесты / учёба" },
      { value: "analytics", label: "1 ГБ – 100 ГБ — аналитика, DWH" },
      { value: "production", label: "100 ГБ – 10 ТБ — продакшн кластер" },
      { value: "petabyte", label: "Петабайты — крупный кластер" },
    ],
    freeTextLabel: "Детали (необязательно)",
    freeTextPlaceholder: "Например: real-time аналитика для e-commerce, 50M событий в день...",
  },
  {
    id: "role",
    title: "Твоя роль",
    subtitle: "Кем ты работаешь или учишься?",
    type: "radio",
    required: true,
    options: [
      { value: "analyst", label: "Аналитик данных" },
      { value: "developer", label: "Разработчик (backend/data)" },
      { value: "student", label: "Студент" },
      { value: "dba", label: "DBA / администрирование БД" },
      { value: "other", label: "Другое" },
    ],
  },
  {
    id: "motivation",
    title: "Твоя цель",
    subtitle: "Зачем ты хочешь углубить знания ClickHouse?",
    type: "radio_motivation",
    required: true,
    options: [
      { value: "interview_prep", label: "Готовлюсь к собеседованию" },
      { value: "career_change", label: "Переквалификация / новая специальность" },
      { value: "upskill", label: "Повышаю квалификацию для текущей работы" },
      { value: "project", label: "Конкретный проект или задача" },
      { value: "curiosity", label: "Просто интересно" },
    ],
    freeTextLabel: "Расскажи подробнее (необязательно)",
    freeTextPlaceholder: "Например: собес в Яндекс через 2 недели, нужно закрыть пробелы в шардировании...",
  },
  {
    id: "concepts",
    title: "Концепции, с которыми ты знаком",
    subtitle: "Отметь всё, с чем уже работал или что хорошо понимаешь",
    type: "checkbox_grid_dynamic",
    freeTextLabel: "Что добавить? (необязательно)",
    freeTextPlaceholder: "Например: Kafka Engine, S3 бэкенд, ClickHouse Cloud...",
  },
];

